import classNames from "classnames";
import React, { FC, FormEvent, useState } from "react";

import { useAppDispatch } from "../../redux/redux";
import {
  TwoStepAppSetup,
  TwoStepPass,
  twoStepSlice,
  useConfirmAppSetupMutation,
  useConfirmEmailSetupMutation,
  useStartAppSetupMutation,
  useStartEmailSetupMutation,
} from "../../slices/TwoStepSlice";
import {
  isCompleteCode,
  twoStepErrorMessage,
  TwoStepMethod,
} from "./twoStep.utils";
import { saveProof } from "./twoStepStorage";
import {
  CodeInput,
  RecoveryCodesPanel,
  TsAlert,
  tsLink,
  tsPrimary,
} from "./TwoStepUi";

type Step = "choose" | "app" | "email" | "codes";

const MethodCard: FC<{
  title: string;
  text: string;
  badge?: string;
  selected: boolean;
  onClick: () => void;
}> = ({ title, text, badge, selected, onClick }) => (
  <button
    type={"button"}
    onClick={onClick}
    aria-pressed={selected}
    className={classNames(
      "flex w-full flex-col gap-1 rounded-[11px] border-[1.5px] bg-white px-4 py-3.5 text-left",
      selected
        ? "border-[#6B4EFF] shadow-[0_0_0_3px_rgba(107,78,255,0.12)]"
        : "border-[#DCE2EA] hover:border-[#C9D0DB]",
    )}>
    <span className={"flex items-center gap-2"}>
      <span className={"text-[14.5px] font-extrabold text-[#1F2433]"}>
        {title}
      </span>
      {badge && (
        <span
          className={
            "rounded-full bg-[#F0EDFF] px-2 py-0.5 text-[11px] font-bold text-[#5B3FE6]"
          }>
          {badge}
        </span>
      )}
    </span>
    <span className={"text-[13px] leading-[1.5] text-[#5B6678]"}>{text}</span>
  </button>
);

/** Changing the method makes new recovery codes (QA batch 18). */
export const CHANGE_METHOD_RECOVERY_NOTE =
  "Changing the method gives you new recovery codes. Your old recovery codes will stop working, so save the new ones when they're shown.";

/**
 * Setting up two-step sign-in: choose authenticator app (QR code + key) or
 * email codes, confirm with a code, then see the 8 recovery codes once.
 * Used by the sign-in screen (when the firm requires it) and the Profile page.
 */
export const TwoStepSetup: FC<{
  email: string;
  current?: TwoStepMethod | null;
  onDone: (pass: TwoStepPass) => void;
  onCancel?: () => void;
  cancelLabel?: string;
}> = ({ email, current, onDone, onCancel, cancelLabel = "Cancel" }) => {
  const [step, setStep] = useState<Step>("choose");
  // Changing an existing method (fixed when the window opens: the profile's
  // settings refresh to "on" right after a first setup).
  const [changing] = useState(!!current);
  const [choice, setChoice] = useState<TwoStepMethod>(
    current === "app" ? "email" : "app",
  );
  const [app, setApp] = useState<TwoStepAppSetup | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pass, setPass] = useState<TwoStepPass | null>(null);
  const [showKey, setShowKey] = useState(false);

  const [startApp, startAppState] = useStartAppSetupMutation();
  const dispatch = useAppDispatch();
  const [confirmApp, confirmAppState] = useConfirmAppSetupMutation();
  const [startEmail, startEmailState] = useStartEmailSetupMutation();
  const [confirmEmail, confirmEmailState] = useConfirmEmailSetupMutation();
  const busy =
    startAppState.isLoading ||
    confirmAppState.isLoading ||
    startEmailState.isLoading ||
    confirmEmailState.isLoading;

  const begin = async () => {
    setError(null);
    setCode("");
    if (choice === "app") {
      const result = await startApp();
      if ("error" in result) return setError(twoStepErrorMessage(result.error));
      setApp(result.data);
      setStep("app");
    } else {
      const result = await startEmail();
      if ("error" in result) return setError(twoStepErrorMessage(result.error));
      setSentTo(result.data.sentTo);
      setStep("email");
    }
  };

  const confirm = async (event: FormEvent) => {
    event.preventDefault();
    if (!isCompleteCode(code)) return setError("Enter the 6-digit code.");
    setError(null);
    const result =
      step === "app"
        ? await confirmApp({ code })
        : await confirmEmail({ code });
    if ("error" in result) return setError(twoStepErrorMessage(result.error));
    // From now on this browser has passed the second step.
    saveProof(email, result.data.proof, result.data.proofExpiresAt);
    // Refresh the two-step screens only now, so they carry the new proof.
    dispatch(
      twoStepSlice.util.invalidateTags([
        "TwoStepSettings",
        "TwoStepFirm",
        "TwoStepTeam",
      ]),
    );
    setPass(result.data);
    setStep("codes");
  };

  const resend = async () => {
    setError(null);
    const result = await startEmail();
    if ("error" in result) return setError(twoStepErrorMessage(result.error));
    setSentTo(result.data.sentTo);
  };

  if (step === "codes" && pass) {
    return (
      <div className={"flex flex-col gap-4"}>
        <TsAlert tone={"success"}>
          Two-step sign-in is on. You&apos;ll enter a code when you sign in.
        </TsAlert>
        <div className={"flex flex-col gap-1"}>
          <span className={"text-[15px] font-extrabold"}>
            Save your recovery codes
          </span>
          <span className={"text-[13px] leading-[1.55] text-[#5B6678]"}>
            If you lose your phone or can&apos;t get emails, each code lets you
            sign in once. Keep them somewhere safe. They are shown only now.
            {changing && " Your old recovery codes no longer work."}
          </span>
        </div>
        <RecoveryCodesPanel codes={pass.recoveryCodes || []} email={email} />
        <button
          type={"button"}
          className={tsPrimary}
          onClick={() => onDone(pass)}>
          I&apos;ve saved my codes
        </button>
      </div>
    );
  }

  if (step === "app" && app) {
    return (
      <form className={"flex flex-col gap-4"} onSubmit={confirm}>
        <ol
          className={
            "m-0 flex list-decimal flex-col gap-1.5 pl-5 text-[13.5px] leading-[1.5] text-[#3A4256]"
          }>
          <li>
            Open Microsoft Authenticator or Google Authenticator on your phone.
          </li>
          <li>Add an account and scan this QR code.</li>
          <li>Type the 6-digit code the app shows.</li>
        </ol>
        <div className={"flex flex-col items-center gap-2"}>
          <img
            src={app.qrImage}
            alt={"QR code for your authenticator app"}
            className={
              "h-[184px] w-[184px] rounded-[11px] border border-[#EDEFF3] p-1.5"
            }
          />
          {showKey ? (
            <div className={"flex flex-col items-center gap-1"}>
              <span className={"text-[12px] font-semibold text-[#6B7185]"}>
                Key for {app.account}
              </span>
              <code
                className={
                  "select-all rounded-md bg-[#F5F6FA] px-2.5 py-1 font-mono text-[13.5px] font-bold tracking-[0.08em] text-[#1F2433]"
                }
                data-testid={"manual-key"}>
                {app.secret}
              </code>
            </div>
          ) : (
            <button
              type={"button"}
              className={tsLink}
              onClick={() => setShowKey(true)}>
              Can&apos;t scan? Enter a key instead
            </button>
          )}
        </div>
        <CodeInput value={code} onChange={setCode} autoFocus={true} />
        {error && <TsAlert>{error}</TsAlert>}
        <button type={"submit"} className={tsPrimary} disabled={busy}>
          {busy ? "Checking…" : "Turn on"}
        </button>
        <button
          type={"button"}
          className={tsLink}
          onClick={() => setStep("choose")}>
          Back
        </button>
      </form>
    );
  }

  if (step === "email") {
    return (
      <form className={"flex flex-col gap-4"} onSubmit={confirm}>
        <span className={"text-[13.5px] leading-[1.55] text-[#3A4256]"}>
          We sent a 6-digit code to <b>{sentTo}</b>. It works for 10 minutes.
        </span>
        <CodeInput value={code} onChange={setCode} autoFocus={true} />
        {error && <TsAlert>{error}</TsAlert>}
        <button type={"submit"} className={tsPrimary} disabled={busy}>
          {busy ? "Checking…" : "Turn on"}
        </button>
        <div className={"flex justify-between"}>
          <button
            type={"button"}
            className={tsLink}
            onClick={() => setStep("choose")}>
            Back
          </button>
          <button
            type={"button"}
            className={tsLink}
            onClick={resend}
            disabled={busy}>
            Send a new code
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className={"flex flex-col gap-3"}>
      <MethodCard
        title={"Authenticator app"}
        badge={"Recommended"}
        text={
          "Microsoft Authenticator or Google Authenticator on your phone shows a new code every 30 seconds. Works without signal."
        }
        selected={choice === "app"}
        onClick={() => setChoice("app")}
      />
      <MethodCard
        title={"Email code"}
        text={`We email a 6-digit code to ${
          email || "your email address"
        } each time you sign in.`}
        selected={choice === "email"}
        onClick={() => setChoice("email")}
      />
      {changing && (
        <TsAlert tone={"warning"}>{CHANGE_METHOD_RECOVERY_NOTE}</TsAlert>
      )}
      {error && <TsAlert>{error}</TsAlert>}
      <button
        type={"button"}
        className={classNames(tsPrimary, "mt-1")}
        onClick={begin}
        disabled={busy}>
        {busy ? "One moment…" : "Continue"}
      </button>
      {onCancel && (
        <button type={"button"} className={tsLink} onClick={onCancel}>
          {cancelLabel}
        </button>
      )}
    </div>
  );
};
